import React, { useState, useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import InvoiceTable from 'common/components/billing/InvoiceTable';

import { api } from 'common/frontend/api';
import { ShopPublicInfo } from 'common/types';
import { useTranslation } from 'services/localization';
import { useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';
import { getShopLocations } from 'common/utils/shopUtils';
import { hashByUniqueField } from 'common/utils/arrays';
import { Invoice } from 'common/modules/billing';

interface Props {
	shopPublicInfo: ShopPublicInfo;
}

const LIMIT = 100;

const Invoices = (props: Props) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { shopPublicInfo } = props;
	const { shopId } = shopPublicInfo;

	const shopLocations = getShopLocations(shopPublicInfo);
	const shopLocationsById = hashByUniqueField(shopLocations, 'id');

	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [invoicesLoading, setInvoicesLoading] = useState<boolean>(true);
	const [invoicesError, setInvoicesError] = useState<boolean>(false);

	const [loadAll, setLoadAll] = useState<boolean>(false);
	const showLoadAll = !loadAll && invoices.length === LIMIT;

	useEffect(() => {
		setInvoicesLoading(true);
		setInvoicesError(false);

		const baseQuery = api()
			.billing.invoices.get.where('merchantId', '==', shopId)
			.orderBy('invoiceNumber', 'desc');

		const query = loadAll ? baseQuery : baseQuery.limit(LIMIT);

		const listener = query.onSnapshot(
			(data) => {
				setInvoices(data);
				setInvoicesLoading(false);
			},
			(err) => {
				setInvoicesError(true);
				setInvoicesLoading(false);
			},
		);

		return () => {
			listener();
		};
	}, [shopId, loadAll]);

	const invoicesWithStoreDetails = invoices.map((invoice) => {
		return {
			...invoice,
			storeName: shopLocationsById[invoice.locationId]?.name ?? invoice.locationId,
		};
	});

	return (
		<Box>
			<Typography variant="h6">{t('account.billing.invoices.title', 'Invoices')}</Typography>
			<Typography variant="body1" color="secondary" gutterBottom>
				{t('account.billing.invoices.description', 'Your latest invoices.')}{' '}
				{showLoadAll && (
					<Link className={classes.loadAllLink} onClick={() => setLoadAll(true)}>
						{t('account.billing.invoices.loadAll', 'Load all')}
					</Link>
				)}
			</Typography>
			<Box mt={3} />
			<InvoiceTable
				data={invoicesWithStoreDetails}
				loading={invoicesLoading}
				error={invoicesError}
				onError={() => {
					dispatch(
						NotificationActions.showNotification({
							variant: 'error',
							message: t('common:errors.somethingWentWrong'),
						}),
					);
				}}
				strings={{
					statusPending: t('account.billing.invoiceTable.pending', 'Pending'),
					statusFailed: t('account.billing.invoiceTable.failed', 'Failed'),
					statusCancelled: t('account.billing.invoiceTable.cancelled', 'Cancelled'),
					statusPaid: t('account.billing.invoiceTable.paid', 'Paid'),
					statusRefunded: t('account.billing.invoiceTable.refunded', 'Refunded'),
					retry: t('account.billing.invoiceTable.retry', 'Retry'),
					errorStateTitle: t(
						'account.billing.invoiceTable.errorStateTitle',
						'Error loading invoices',
					),
					errorStateDescription: t(
						'account.billing.invoiceTable.errorStateDescription',
						'Please refresh the page to try again',
					),
					emptyStateTitle: t('account.billing.invoiceTable.emptyStateTitle', 'No data'),
					emptyStateDescription: t(
						'account.billing.invoiceTable.emptyStateDescription',
						'No invoices to show',
					),
					headerBillingCycle: t('account.billing.invoiceTable.billingCycle', 'Billing cycle'),
					headerDateIssued: t('account.billing.invoiceTable.dateIssued', 'Date issued'),
					headerStore: t('common:shopLocation.store'),
					headerInvoiceNo: t('account.billing.invoiceTable.invoiceNo', 'Invoice #'),
					headerStatus: t('account.billing.invoiceTable.status', 'Status'),
					headerAmount: t('account.billing.invoiceTable.amount', 'Amount'),
					download: t('account.billing.invoiceTable.download', 'Download'),
					downloadFailed: t(
						'account.billing.invoiceTable.downloadFailed',
						'Download failed. Click to retry.',
					),
				}}
			/>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		loadAllLink: {
			cursor: 'pointer',
		},
	}),
);

export default Invoices;
